import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from '@mui/material';

import { useCompanyBillingModel, useSetCompanyBillingModel, BillingModel } from '@api';

/**
 * How this agency is sold.
 *
 * Deliberately not a billing control: the invoice is consumption-based for
 * every account. What this decides is whether the **seat gate** runs — the
 * licence cron and the two login paths that can deactivate an agency's
 * negotiators when its seat count or its licence dates say so.
 *
 * Superadmin only, rendered beside the CRM credential panel, because moving an
 * agency onto consumption billing switches off an enforcement mechanism.
 */

interface BillingModelPanelProps {
  companyId?: string;
  /** Lets the parent hide the licence summary when there is no licence. */
  onChange?: (model: BillingModel) => void;
}

export const BillingModelPanel: React.FC<BillingModelPanelProps> = ({ companyId, onChange }) => {
  const { data: model, isLoading } = useCompanyBillingModel(companyId);
  const setModel = useSetCompanyBillingModel(companyId);

  React.useEffect(() => {
    if (model) onChange?.(model);
  }, [model, onChange]);

  if (!companyId) return null;

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const current: BillingModel = model ?? 'license';

  const handleChange = (event: SelectChangeEvent) => {
    const next = event.target.value as BillingModel;
    if (next === current) return;
    setModel.mutate(next, { onSuccess: () => onChange?.(next) });
  };

  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <Typography variant="subtitle2">Billing model</Typography>
        <Chip
          size="small"
          color={current === 'token' ? 'success' : 'default'}
          label={current === 'token' ? 'Consumption' : 'Licensed seats'}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Both models are invoiced on SRT consumption. This setting decides whether the agency is
        <strong> also </strong>
        limited by licensed seats — a seat count and an expiry date that can deactivate its
        negotiators.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center">
        <Select
          size="small"
          value={current}
          onChange={handleChange}
          disabled={setModel.isPending}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="license">Licensed seats + tokens</MenuItem>
          <MenuItem value="token">Tokens only</MenuItem>
        </Select>
        {setModel.isPending && <CircularProgress size={18} />}
      </Stack>

      {current === 'token' && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          No seat limit and no expiry apply. This agency needs no licence record, and its
          negotiators are never deactivated by the licence job. To stop billing it, set its billing
          account to <strong>inactive</strong>.
        </Alert>
      )}

      {setModel.isError && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {(setModel.error as Error)?.message} Nothing was changed.
        </Alert>
      )}
    </Box>
  );
};

export default BillingModelPanel;
